import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import i18n from '../i18n/config';
import { sendPasswordResetEmail } from '../utils/notificationHelpers';

const AuthContext = createContext(null);

const AUTH_STORAGE_KEY = 'managehub_auth_user';

export const AuthProvider = ({ children }) => {
  // Sync initialization from localStorage to prevent flicker
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Failed to parse stored user', e);
      return null;
    }
  });

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Helper to persist user to localStorage
  const persistUser = (userData) => {
    if (userData) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
      setUser(userData);

      // Also set a cookie for server-side persistence if needed by the hosting environment
      const expiry = new Date();
      expiry.setDate(expiry.getDate() + 7); // 7 days
      document.cookie = `mh_auth_persistence=true; expires=${expiry.toUTCString()}; path=/; SameSite=Lax`;
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setUser(null);
      document.cookie = "mh_auth_persistence=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      // Since we already initialized 'user' from localStorage in the useState hook,
      // we just need to validate it with the server.
      await checkAuth(user);
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Load and apply user language preference
  const loadLanguagePreference = async (userId) => {
    if (!userId) return;

    try {
      const { data, error } = await window.ezsite.apis.tablePage(70673, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'user_id', op: 'Equal', value: userId }]
      });

      if (!error && data?.List?.[0]?.language) {
        const userLanguage = data.List[0].language;
        if (userLanguage && (userLanguage === 'en' || userLanguage === 'el')) {
          if (i18n.language !== userLanguage) {
            await i18n.changeLanguage(userLanguage);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load language preference:', error);
    }
  };

  const checkAuth = async (cachedUser = null) => {
    try {
      // First, try to get system user info
      const { data, error } = await window.ezsite.apis.getUserInfo();

      let currentUser = null;
      let email = null;

      // If we have system auth data, use it as base
      if (!error && data) {
        email = (data.Email || data.email || '').toLowerCase().trim();
        const id = data.ID || data.id;
        const systemRole = data.Role || data.role;

        currentUser = {
          id: id,
          email: email,
          name: data.Name || data.name || email,
          role: systemRole,
          is_system_admin: systemRole === 'r-4ZodVZ' || systemRole === 'Administrator'
        };
      } else if (cachedUser) {
        // If system auth check fails but we have a cached user,
        // we might be in a state where the session needs to be re-established.
        // We'll trust the cache for now but try to verify the profile.
        email = cachedUser.email;
      }

      // Fetch user data from the users table (70648) - primary source of truth
      if (email) {
        try {
          const { data: userData } = await window.ezsite.apis.tablePage(70648, {
            PageNo: 1,
            PageSize: 1,
            Filters: [
            { name: 'email', op: 'Equal', value: email }]

          });

          if (userData?.List?.[0]) {
            const dbUser = userData.List[0];
            const isActive = dbUser.is_active ?? dbUser.IsActive ?? true;

            // Check if account is active
            if (!isActive && !currentUser?.is_system_admin) {
              persistUser(null);
              return null;
            }

            // Build complete user object from users table
            currentUser = {
              id: dbUser.id || dbUser.ID || currentUser?.id || cachedUser?.id,
              email: dbUser.email || dbUser.Email || email,
              name: dbUser.name || dbUser.Name || currentUser?.name || cachedUser?.name || email,
              property_id: dbUser.property_id || dbUser.PropertyID,
              building_id: dbUser.building_id || dbUser.BuildingID,
              unit: dbUser.unit || dbUser.Unit,
              role: dbUser.role || dbUser.Role,
              is_active: isActive,
              created_at: dbUser.created_at || dbUser.CreatedAt,
              is_system_admin: currentUser?.is_system_admin || cachedUser?.is_system_admin || false
            };

            persistUser(currentUser);
            if (currentUser.id) {
              await loadLanguagePreference(currentUser.id);
            }
            return currentUser;
          } else if (currentUser && !currentUser.is_system_admin) {
            // User has system auth but not in users table and not admin
            persistUser(null);
            return null;
          }
        } catch (e) {
          console.error('Error fetching user profile during check:', e);
          // If we have a cached user and the network fails, keep the cached user
          if (cachedUser) return cachedUser;
          persistUser(null);
          return null;
        }
      }

      // If we reached here without a currentUser but had a cachedUser,
      // it means the system auth failed AND we couldn't find/verify the profile.
      if (!currentUser && cachedUser) {








        // Optional: you could be more lenient here, but safety first
        // persistUser(null); 
      } else if (!currentUser) {persistUser(null);}return currentUser;} catch (error) {console.error('Auth check failed:', error);if (!cachedUser) {persistUser(null);
      }
      return cachedUser;
    }
  };

  const login = async (email, password) => {
    try {
      const cleanEmail = email.toLowerCase().trim();

      // STEP 1: Validate credentials against the users table (70648)
      const { data: userData, error: fetchError } = await window.ezsite.apis.tablePage(70648, {
        PageNo: 1,
        PageSize: 1,
        Filters: [
        { name: 'email', op: 'Equal', value: cleanEmail }]

      });

      if (fetchError) {
        throw new Error('Failed to validate credentials. Please try again.');
      }

      const userRecord = userData?.List?.[0];

      if (!userRecord) {
        throw new Error('Invalid email or password');
      }

      const isActive = userRecord.is_active ?? userRecord.IsActive ?? true;
      if (!isActive) {
        throw new Error('Your account has been deactivated. Please contact your property manager.');
      }

      // STEP 2: Validate password
      const { data: authCheck, error: authError } = await window.ezsite.apis.tablePage(70648, {
        PageNo: 1,
        PageSize: 1,
        Filters: [
        { name: 'email', op: 'Equal', value: cleanEmail },
        { name: 'password', op: 'Equal', value: password, connector: 'And' }]

      });

      if (authError || !authCheck?.List?.[0]) {
        throw new Error('Invalid email or password');
      }

      // STEP 3: Sync with system auth
      let systemAuthSuccess = false;
      try {
        const { error: sysError } = await window.ezsite.apis.login({
          email: cleanEmail,
          password
        });
        systemAuthSuccess = !sysError;
      } catch (sysError) {
        console.warn('System auth failed:', sysError);
      }

      // STEP 4: Build authenticated user object
      const authenticatedUser = {
        id: userRecord.id || userRecord.ID,
        email: userRecord.email || userRecord.Email || cleanEmail,
        name: userRecord.name || userRecord.Name || cleanEmail,
        property_id: userRecord.property_id || userRecord.PropertyID,
        building_id: userRecord.building_id || userRecord.BuildingID,
        unit: userRecord.unit || userRecord.Unit,
        role: userRecord.role || userRecord.Role,
        is_active: isActive,
        created_at: userRecord.created_at || userRecord.CreatedAt,
        is_system_admin: false
      };

      if (systemAuthSuccess) {
        try {
          const { data: sysUserInfo } = await window.ezsite.apis.getUserInfo();
          if (sysUserInfo) {
            const systemRole = sysUserInfo.Role || sysUserInfo.role;
            authenticatedUser.is_system_admin = systemRole === 'r-4ZodVZ' || systemRole === 'Administrator';
          }
        } catch (e) {
          console.warn('Could not fetch system user info:', e);
        }
      }

      persistUser(authenticatedUser);
      await loadLanguagePreference(authenticatedUser.id);
      return true;
    } catch (error) {
      console.error('Login process error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await window.ezsite.apis.logout();
    } catch (e) {
      console.warn('Logout API error', e);
    }
    persistUser(null);
    navigate('/login', { replace: true });
  };

  /**
   * Updates the authenticated user's profile information in the users table.
   * @param {Object} updates - The fields to update (name, unit, property_id, building_id, etc.)
   */
  const updateUserProfile = async (updates) => {
    if (!user?.id) {
      throw new Error('User session not found');
    }

    try {
      const { error } = await window.ezsite.apis.tableUpdate(70648, {
        id: Number(user.id),
        ...updates
      });

      if (error) throw error;

      // Update local state and persistence
      const updatedUser = { ...user, ...updates };
      persistUser(updatedUser);

      toast.success(i18n.t('profile.updateSuccess') || 'Profile updated successfully');
      return updatedUser;
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(i18n.t('profile.updateFailed') || 'Failed to update profile');
      throw error;
    }
  };

  /**
   * Updates the authenticated user's password.
   */
  const changePassword = async (currentPassword, newPassword) => {
    if (!user?.email || !user?.id) throw new Error('User session not found');

    try {
      // 1. Verify current password
      const { data: authCheck, error: authError } = await window.ezsite.apis.tablePage(70648, {
        PageNo: 1,
        PageSize: 1,
        Filters: [
        { name: 'email', op: 'Equal', value: user.email },
        { name: 'password', op: 'Equal', value: currentPassword, connector: 'And' }]

      });

      if (authError || !authCheck?.List?.[0]) {
        throw new Error(i18n.t('profile.invalidCurrentPassword'));
      }

      // 2. Update password in the database
      const { error: updateError } = await window.ezsite.apis.tableUpdate(70648, {
        id: Number(user.id),
        password: newPassword
      });

      if (updateError) throw updateError;

      // 3. Try to sync with system password if possible
      try {
        await window.ezsite.apis.changePassword({
          oldPassword: currentPassword,
          newPassword: newPassword
        });
      } catch (e) {
        console.warn('System password sync failed:', e);
      }

      toast.success(i18n.t('profile.passwordChanged'));
      return true;
    } catch (error) {
      console.error('Password change error:', error);
      toast.error(error.message || i18n.t('profile.passwordChangeFailed'));
      throw error;
    }
  };

  /**
   * Requests a password reset for the given email.
   */
  const requestPasswordReset = async (email) => {
    try {
      const cleanEmail = email.toLowerCase().trim();

      // Check if user exists
      const { data: userData, error: fetchError } = await window.ezsite.apis.tablePage(70648, {
        PageNo: 1,
        PageSize: 1,
        Filters: [
        { name: 'email', op: 'Equal', value: cleanEmail }]

      });

      if (fetchError) throw fetchError;

      const userRecord = userData?.List?.[0];

      // If user exists, trigger the email
      if (userRecord) {
        await sendPasswordResetEmail(cleanEmail, userRecord.name || userRecord.Name);
      } else {
        // Log for debugging but return success for security
        console.warn(`[Auth] Password reset requested for non-existent email: ${cleanEmail}`);
      }

      // Even if user doesn't exist, we return true for security reasons
      // to prevent email enumeration.
      return true;
    } catch (error) {
      console.error('Request password reset error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, updateUserProfile, changePassword, requestPasswordReset }}>
      {children}
    </AuthContext.Provider>);

};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};